<template>
  <v-menu v-model="menu" :close-on-content-click="false" offset-y>
    <template v-slot:activator="{ on, attrs }">
      <v-icon v-bind="attrs" v-on="on" color="silver-chalice" style="cursor: pointer">
        {{ localTags.length ? 'mdi-tag' : 'mdi-tag-outline' }}
      </v-icon>
    </template>
    <v-card class="pa-4" style="width: 340px">
      <div class="text-subtitle-2 mb-2">{{ 'tags'|localize }}</div>
      <v-combobox
        class="request-tags-cell__combobox"
        v-model="selectedTags"
        :items="tagsList"
        item-text="name"
        return-object
        multiple
        small-chips
        deletable-chips
        dense
        hide-details
        outlined
        :value-comparator="tagsMatch"
        :loading="loading"
        :disabled="loading"
        :placeholder="'addTag'|localize"
        @change="onChange"
      >
        <template v-slot:item="{ item }">
          <v-list-item-title>{{ item.name }}</v-list-item-title>
        </template>
      </v-combobox>
    </v-card>
  </v-menu>
</template>

<script>
  import api from '@/api/api';
  import methods from '@/helpers/methods';

  export default {
    name: 'RequestTagsCell',

    props: ['requestData', 'tagsList'],

    data() {
      return {
        menu: false,
        loading: false,
        localTags: this.requestData.tags || [],
        selectedTags: this.requestData.tags || [],
      };
    },

    methods: {
      // Combobox items can be either an existing tag object (picked from the
      // suggestions list) or a plain string (a brand-new value typed by the user).
      tagName(value) {
        let name = value && typeof value === 'object' ? value.name : value;
        return typeof name === 'string' ? name.trim() : name;
      },
      // v-combobox uses this both to compare a single item against a single
      // value (preselecting/toggling items in the dropdown, so array assigned
      // tags and the (differently instanced) tagsList entries still match by
      // name) and to compare the whole selection array before/after a change
      // (Vuetify's own setValue guard), hence the array branch below.
      tagsMatch(a, b) {
        if (Array.isArray(a) || Array.isArray(b)) {
          if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) {
            return false;
          }
          return a.every((item, index) => this.tagsMatch(item, b[index]));
        }
        let nameA = this.tagName(a);
        let nameB = this.tagName(b);
        if (typeof nameA === 'string') nameA = nameA.toLowerCase();
        if (typeof nameB === 'string') nameB = nameB.toLowerCase();
        return nameA === nameB;
      },
      onChange(values) {
        let names = values.map(this.tagName).filter(Boolean);
        let seen = new Set();
        let uniqueNames = names.filter((name) => {
          let key = name.toLowerCase();
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
        this.saveTags(uniqueNames);
      },
      async saveTags(names) {
        try {
          this.loading = true;
          let response = await api.updateRequestTags(this.requestData.id, names);
          let updatedTags = response.data || [];
          this.localTags = updatedTags;
          this.selectedTags = updatedTags;
          this.$emit('updateRequestTags', updatedTags);
          // Any save can add/remove tags from the shared list (a brand-new tag name
          // creates one), so the parent always refreshes its cached tags list here.
          this.$emit('tagsChanged');
        } catch (e) {
          methods.showGlobalSnackbar('error', methods.getErrors(e.response));
          this.selectedTags = this.localTags;
        } finally {
          this.loading = false;
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  .request-tags-cell__combobox {
    ::v-deep .v-chip {
      margin: 2px 4px 2px 0;
    }
  }
</style>
